import { FeatureToggles } from '@/composables/useFeatureToggle';
import { isFeatureEnabled } from '@/helpers/FeatureToggle/featureToggleHelper';
import { OfferType, PropertyTypeNumber } from '@/models/valuationFunnel';
import { PropertyValuationData } from '@/stores/types/propertyValuation';
import { BUILD_YEAR_THRESHOLD, ValuationFlow } from '@/types';

interface NavigationRule {
  next: (formData: PropertyValuationData) => ValuationFlow | null;
  prev: (formData: PropertyValuationData) => ValuationFlow | null;
  cleanupFields?: (formData: PropertyValuationData) => Partial<PropertyValuationData>;
}

/**
 * Helper function to check if PropertyOwnership step should be skipped
 */
const shouldSkipPropertyOwnership = (): boolean => {
  return isFeatureEnabled(FeatureToggles.SKIP_PROPERTY_OWNERSHIP);
};

export const FLOW_RULES: Record<ValuationFlow, NavigationRule> = {
  [ValuationFlow.PropertyType]: {
    next: () => ValuationFlow.OfferType,
    prev: () => null,
    cleanupFields: ({ propertyType }) => ({
      ...(propertyType !== PropertyTypeNumber.House && {
        lotSize: undefined,
      }),
    }),
  },
  [ValuationFlow.OfferType]: {
    next: () => ValuationFlow.PostalCode,
    prev: () => ValuationFlow.PropertyType,
    cleanupFields: ({ offerType }) => ({
      ...(offerType !== OfferType.Selling && {
        whenToSell: undefined,
      }),
      ...(offerType === OfferType.Buying && {
        isPropertyOwner: undefined,
      }),
    }),
  },
  [ValuationFlow.PostalCode]: {
    next: () => ValuationFlow.StreetAndNumber,
    prev: () => ValuationFlow.OfferType,
  },
  [ValuationFlow.StreetAndNumber]: {
    next: () => ValuationFlow.PropertyDetails,
    prev: () => ValuationFlow.PostalCode,
  },
  [ValuationFlow.PropertyDetails]: {
    next: ({ buildYear, offerType }) => {
      if (buildYear! < BUILD_YEAR_THRESHOLD) {
        return ValuationFlow.Renovation;
      }

      if (offerType === OfferType.Buying) {
        return ValuationFlow.ContactDetails;
      }

      // Skip PropertyOwnership if feature flag is enabled
      if (shouldSkipPropertyOwnership()) {
        return offerType === OfferType.Selling
          ? ValuationFlow.WhenToSell
          : ValuationFlow.ContactDetails;
      }

      return ValuationFlow.PropertyOwnership;
    },
    prev: () => ValuationFlow.StreetAndNumber,
    cleanupFields: ({ buildYear }) => ({
      ...(buildYear! >= BUILD_YEAR_THRESHOLD && {
        isRenovated: false,
        renovationYear: undefined,
      }),
    }),
  },
  [ValuationFlow.Renovation]: {
    next: ({ isRenovated, offerType }) => {
      if (isRenovated) {
        return ValuationFlow.RenovationYear;
      }

      if (offerType === OfferType.Buying) {
        return ValuationFlow.ContactDetails;
      }

      // Skip PropertyOwnership if feature flag is enabled
      if (shouldSkipPropertyOwnership()) {
        return offerType === OfferType.Selling
          ? ValuationFlow.WhenToSell
          : ValuationFlow.ContactDetails;
      }

      return ValuationFlow.PropertyOwnership;
    },
    prev: () => ValuationFlow.PropertyDetails,
    cleanupFields: ({ isRenovated }) => ({
      ...(!isRenovated && {
        renovationYear: undefined,
      }),
    }),
  },
  [ValuationFlow.RenovationYear]: {
    next: ({ offerType }) => {
      if (offerType === OfferType.Buying) {
        return ValuationFlow.ContactDetails;
      }

      // Skip PropertyOwnership if feature flag is enabled
      if (shouldSkipPropertyOwnership()) {
        return offerType === OfferType.Selling
          ? ValuationFlow.WhenToSell
          : ValuationFlow.ContactDetails;
      }

      return ValuationFlow.PropertyOwnership;
    },
    prev: () => ValuationFlow.Renovation,
  },
  [ValuationFlow.PropertyOwnership]: {
    next: ({ offerType, isPropertyOwner }) => {
      // When PropertyOwnership step is shown, follow original logic
      // Only property owners in selling flow get WhenToSell
      return offerType === OfferType.Selling && isPropertyOwner === true
        ? ValuationFlow.WhenToSell
        : ValuationFlow.ContactDetails;
    },
    prev: ({ buildYear, isRenovated }) =>
      buildYear! < BUILD_YEAR_THRESHOLD
        ? isRenovated
          ? ValuationFlow.RenovationYear
          : ValuationFlow.Renovation
        : ValuationFlow.PropertyDetails,
    cleanupFields: ({ offerType, isPropertyOwner }) => ({
      ...(offerType === OfferType.Selling &&
        isPropertyOwner === false && {
          whenToSell: undefined,
        }),
    }),
  },
  [ValuationFlow.WhenToSell]: {
    next: () => ValuationFlow.ContactDetails,
    prev: ({ buildYear, isRenovated }) => {
      // If PropertyOwnership is skipped, navigate back to the previous step
      if (shouldSkipPropertyOwnership()) {
        return buildYear! < BUILD_YEAR_THRESHOLD
          ? isRenovated
            ? ValuationFlow.RenovationYear
            : ValuationFlow.Renovation
          : ValuationFlow.PropertyDetails;
      }
      return ValuationFlow.PropertyOwnership;
    },
  },
  [ValuationFlow.ContactDetails]: {
    next: () => ValuationFlow.PhoneNumber,
    prev: ({ offerType, buildYear, isRenovated, isPropertyOwner }) => {
      const shouldShowPropertyOwnerShipStep =
        offerType !== OfferType.Buying && !shouldSkipPropertyOwnership();

      // When PropertyOwnership is skipped, treat isPropertyOwner as always true for selling flows
      // When PropertyOwnership is enabled, only show WhenToSell if user is actually property owner
      const shouldShowWhenToSellStep =
        offerType === OfferType.Selling &&
        (shouldSkipPropertyOwnership() || isPropertyOwner === true);

      return shouldShowWhenToSellStep
        ? ValuationFlow.WhenToSell
        : shouldShowPropertyOwnerShipStep
          ? ValuationFlow.PropertyOwnership
          : buildYear! < BUILD_YEAR_THRESHOLD
            ? isRenovated
              ? ValuationFlow.RenovationYear
              : ValuationFlow.Renovation
            : ValuationFlow.PropertyDetails;
    },
  },
  [ValuationFlow.PhoneNumber]: {
    next: () => null,
    prev: () => ValuationFlow.ContactDetails,
  },
  [ValuationFlow.CodeInput]: {
    next: () => null,
    prev: () => null,
  },
};
